/* eslint-disable no-param-reassign */
import { extend } from 'underscore';
import React, { useEffect, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import DateSettings from '@bingads-webui-campaign-legacy/ad-schedule/view/date-settings';
import { datePickerStyle, advancedSettingsSectionStyle, toggleButtonStyle } from '@bingads-webui-campaign-react/common-styles';
import { creationCommonStyle } from '@bingads-webui-campaign-react/campaign-adgroup-settings-shared-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';

const globalLegacyI18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/campaign-creation-advanced');

const getStyles = (__, core) =>
  ({
    '@global': {
      ...datePickerStyle(core),
    },
    dateSettingContainer: {
      '& .advanced-scheduling-grid': {
        width: '918px',
      },
      ...creationCommonStyle(core),
      ...advancedSettingsSectionStyle(core),
      '& .radio-inline': {
        display: 'block',
      },
      '& .adschedule-targets-container': {
        borderTop: 'unset',
        paddingTop: '0px',
      },
      '& .toggle-campaign-settings-switch.form-control-static[data-bind]': {
        display: 'inline-block',
        textDecoration: 'none',
        paddingRight: '8px',
        float: 'left',
      },
      ...toggleButtonStyle(core),
      '& .date-settings-intro': {
        paddingLeft: 0,
      },
      '& .date-settings': {
        '& .col-sm-12': {
          paddingLeft: 0,
        },
        '& table': {
          width: '500px',
          '& td': {
            width: '100%',
            display: 'block',
          },
        },
      },
      '& .alert-danger': {
        backgroundColor: 'unset',
        color: '#A80000',
      },
      '& table tbody .col-sm-9': {
        width: 'unset',
      },
      '& table tbody .item-label.schedule-item-label.form-control-static': {
        fontWeight: '600',
        color: '#323130',
        paddingTop: '10px',
        display: 'block',
      },
      '& .adschedule-targets-container .advanced-scheduling-grid .ad-scheduling-panel table tr:nth-child(3) td .radio-inline:nth-child(2)': {
        top: 'unset',
      },
      '& .form-control-static': {
        margin: '3px 0',
        padding: 0,
        minHeight: 'fit-content',
        color: core.palette.neutralSecondary,
        marginBottom: '3px !important',
        width: 'unset',
      },
      '& td>.col-sm-9': {
        padding: 0,
      },
    },
  });

// reference: private/campaign-legacy/packages/campaign-targeting/view/campaign-targeting.js
export const LegacyDateSetting = React.memo((props) => {
  const {
    data,
    onChange,
    activityLogger,
    allowNoEndDateSelection,
    allowStartDateSelection,
    allowEndDateSelection,
  } = props;
  const view = useRef();
  let container;
  const classes = useDefaultStyles(getStyles);

  useEffect(() => {
    view.current = new DateSettings(extend(
      {
        callOnChangeCb: onChange,
      },
      {
        startDate: data.StartDate ? globalLegacyI18n.formatDate(data.StartDate, {
          raw: 'yyyy-MM-dd',
        }) : null,
        endDate: data.EndDate ? globalLegacyI18n.formatDate(data.EndDate, {
          raw: 'yyyy-MM-dd',
        }) : null,
        timeZone: data.TimeZone,
        isCreate: data.isCreate,
        activityLogger,
        allowNoEndDateSelection,
        allowStartDateSelection,
        allowEndDateSelection,
      }
    ));

    view.current.render();
    view.current.$el.appendTo(container);

    return () => {
      view.current.remove();
    };
    // We will refresh the view if data changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (view.current) {
      // Populate default value. If user did not specify StartDate, it will be set to current date.
      if (!data.StartDate) {
        onChange(view.current.viewModel.getRawValues());
      }
    }
  });

  return (
    <div
      className={classnames(classes.dateSettingContainer)}
    >
      <div className="date-setting-container">
        <div ref={(ref) => { container = ref; }} />
      </div>
    </div>
  );
});

LegacyDateSetting.propTypes = {
  data: PropTypes.shape({
    StartDate: PropTypes.string,
    EndDate: PropTypes.string,
    TimeZone: PropTypes.string,
    isCreate: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  activityLogger: PropTypes.shape({
    signal: PropTypes.func,
  }),
  allowNoEndDateSelection: PropTypes.bool,
  allowStartDateSelection: PropTypes.bool,
  allowEndDateSelection: PropTypes.bool,
};

LegacyDateSetting.defaultProps = {
  activityLogger: null,
  allowNoEndDateSelection: true,
  allowStartDateSelection: true,
  allowEndDateSelection: true,
};
